import React, { useCallback, useEffect, useRef, useState } from 'react'

import mapboxgl, { LngLat, LngLatBounds, LngLatLike } from 'mapbox-gl'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { MapRef, Marker, MarkerDragEvent } from 'react-map-gl/mapbox'

import { IProjectAddressInput } from '~/client/graph'
import {
  getMapBoxUrl,
  mapboxFeatureToAddress,
} from '~/client/src/shared/components/MapBoxViewer/mapboxAddress'
import { getMapBoxPlaceFromCoordinates } from '~/client/src/shared/components/MapBoxViewer/mapboxGeocoding'
import MapCanvas from '~/client/src/shared/components/MapBoxViewer/MapCanvas'
import { MapControls } from '~/client/src/shared/components/MapControls/MapControls'
import MapControlsStore from '~/client/src/shared/components/MapControls/MapControlsStore'
import DomPin from '~/client/src/shared/components/SitemapHelpers/components/DomPins/DomPin'
import { LOCATION_ICON_COMPONENTS } from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  LNG_LAT_DISCREPANCY_METERS,
  toAddressBoundsInput,
} from '~/client/src/shared/utils/Address'

export async function getMapBoxPlaces(
  query: string,
  token: string,
  proximity?: LngLatLike,
): Promise<GeoJSON.FeatureCollection> {
  if (!query.trim()) {
    return { features: [], type: 'FeatureCollection' }
  }

  const resp = await fetch(getMapBoxUrl(query, token, true, proximity))
  return await resp.json()
}

export function areLngLatEqual(a: LngLat, b: LngLat) {
  if (!a || !b) {
    return !a === !b
  }

  return a.distanceTo(b) < LNG_LAT_DISCREPANCY_METERS
}

export function areLngLatBoundsEqual(a: LngLatBounds, b: LngLatBounds) {
  if (!a || !b) {
    return !a === !b
  }

  return (
    areLngLatEqual(a.getNorthEast(), b.getNorthEast()) &&
    areLngLatEqual(a.getSouthWest(), b.getSouthWest())
  )
}

const ProjectIcon = LOCATION_ICON_COMPONENTS[MapViewLocationIcon.Project]

interface IMapBoxViewProps {
  className?: string
  bearing: number
  center: LngLat
  zoom: number

  onAddressChanged?: (address: IProjectAddressInput) => void
  onDataChanged?: (
    bearing: number,
    bounds: LngLatBounds,
    center: LngLat,
    zoom: number,
    pitch: number,
  ) => void
}

export const MapBoxView = observer(function MapBoxView(
  props: IMapBoxViewProps,
) {
  const { className } = props
  const mapRef = useRef<MapRef | null>(null)
  const isProgrammaticMove = useRef(false)

  const [markerCoords, setMarkerCoords] = useState({
    latitude: props.center.lat,
    longitude: props.center.lng,
  })

  const [store] = useState(
    () =>
      new MapControlsStore({
        latitude: props.center.lat,
        longitude: props.center.lng,
        zoom: props.zoom || 15,
      }),
  )

  useEffect(() => () => store.dispose(), [store])

  const handleMapRef = useCallback(
    (ref: MapRef | null) => {
      mapRef.current = ref
      store.setMapRef(ref)
    },
    [store],
  )

  const getUpdatedAddress = useCallback(
    async (lngLat: { lng: number; lat: number }) => {
      const map = mapRef.current?.getMap()
      if (!map) return

      const coords = new LngLat(lngLat.lng, lngLat.lat)
      const item = await getMapBoxPlaceFromCoordinates(
        coords,
        mapboxgl.accessToken,
      )
      const addr = mapboxFeatureToAddress(item)
      addr.bearing = map.getBearing()
      addr.bounds = toAddressBoundsInput(map.getBounds())
      addr.center = coords

      props.onAddressChanged?.(addr)
    },
    [props.onAddressChanged],
  )

  const handleDragEnd = useCallback(
    (event: MarkerDragEvent) => {
      const { lng, lat } = event.lngLat
      setMarkerCoords({ latitude: lat, longitude: lng })
      getUpdatedAddress({ lng, lat })
    },
    [getUpdatedAddress],
  )

  const handleMoveEnd = useCallback(() => {
    if (isProgrammaticMove.current) {
      isProgrammaticMove.current = false
      return
    }

    const map = mapRef.current?.getMap()
    if (!map) return

    const markerLngLat = new LngLat(
      markerCoords.longitude,
      markerCoords.latitude,
    )

    props.onDataChanged?.(
      map.getBearing(),
      map.getBounds(),
      markerLngLat,
      map.getZoom(),
      map.getPitch(),
    )
  }, [props.onDataChanged, markerCoords])

  const centerKey = props.center
    ? `${props.center.lng},${props.center.lat}`
    : ''

  useEffect(() => {
    const map = mapRef.current?.getMap()
    if (!map) return

    let moved = false

    if (map.getBearing() !== props.bearing) {
      map.setBearing(props.bearing)
      moved = true
    }

    if (
      !areLngLatEqual(
        new LngLat(markerCoords.longitude, markerCoords.latitude),
        props.center,
      )
    ) {
      setMarkerCoords({
        latitude: props.center.lat,
        longitude: props.center.lng,
      })
      map.setCenter(props.center)
      moved = true
    }

    const targetZoom = props.zoom || 15
    if (Math.abs(map.getZoom() - targetZoom) > 0.01) {
      map.setZoom(targetZoom)
      moved = true
    }

    if (moved) {
      isProgrammaticMove.current = true
      store.updateInitialViewport({
        latitude: props.center.lat,
        longitude: props.center.lng,
        zoom: props.zoom || 15,
      })
    }
  }, [centerKey, props.bearing, props.zoom])

  return (
    <div
      className={classList({
        'setup-form-map': true,
        [className]: !!className,
      })}
    >
      <div className="setup-form-map-container tw:flex-1 tw:min-h-[300px] tw:min-w-[300px] tw:ml-2.5 tw:mt-3 tw:mb-6 tw:flex tw:flex-col tw:rounded-lg tw:overflow-hidden">
        <MapControls.Provider store={store}>
          <MapCanvas
            ref={handleMapRef}
            initialViewState={{
              longitude: props.center.lng,
              latitude: props.center.lat,
              zoom: props.zoom || 15,
              bearing: props.bearing,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={store.mapStyle}
            projection={store.projection}
            isTrafficEnabled={store.globeViewStyle.isTrafficEnabled}
            isTerrainEnabled={store.globeViewStyle.isTerrainEnabled}
            isThreeDEnabled={store.globeViewStyle.isThreeDEnabled}
            shouldHideLabels={store.globeViewStyle.shouldHideLabels}
            mapboxAccessToken={mapboxgl.accessToken}
            onMoveEnd={handleMoveEnd}
          >
            <Marker
              latitude={markerCoords.latitude}
              longitude={markerCoords.longitude}
              draggable
              onDragEnd={handleDragEnd}
              anchor="bottom"
              className="tw:cursor-grab"
            >
              <DomPin
                fill="var(--primary)"
                stroke="white"
                glyph={({ size, color }) => (
                  <ProjectIcon size={size} color={color} />
                )}
              />
            </Marker>
          </MapCanvas>
          <MapControls.Chrome />
        </MapControls.Provider>
      </div>
    </div>
  )
})
